<script setup>
import { computed } from 'vue';

const props = defineProps({
  data: { type: Object, default: () => ({}) },
});

const largestSdrTotal = computed(() =>
  Math.max(...(props.data.by_sdr || []).map(row => row.leads), 1)
);

const formatDate = value => value?.split('-').reverse().join('/') || '';

const scheduledFromPreviousCards = computed(() =>
  Math.max(
    (props.data.summary?.scheduled || 0) -
      (props.data.summary?.scheduled_from_new_cards || 0),
    0
  )
);

const metrics = [
  [
    'new_customers',
    'DEAL_REPORTS.OPERATIONS.NEW_CUSTOMERS',
    'i-lucide-user-plus',
    'DEAL_REPORTS.OPERATIONS.TOOLTIPS.NEW_CUSTOMERS',
  ],
  [
    'cards_created',
    'DEAL_REPORTS.OPERATIONS.CARDS_CREATED',
    'i-lucide-layout-panel-top',
    'DEAL_REPORTS.OPERATIONS.TOOLTIPS.CARDS_CREATED',
  ],
  [
    'scheduled',
    'DEAL_REPORTS.OPERATIONS.SCHEDULED',
    'i-lucide-calendar-check',
    'DEAL_REPORTS.OPERATIONS.TOOLTIPS.SCHEDULED',
  ],
  [
    'open',
    'DEAL_REPORTS.OPERATIONS.OPEN_CARDS',
    'i-lucide-clock-3',
    'DEAL_REPORTS.OPERATIONS.TOOLTIPS.OPEN_CARDS',
  ],
  [
    'lost',
    'DEAL_REPORTS.OPERATIONS.LOST',
    'i-lucide-circle-x',
    'DEAL_REPORTS.OPERATIONS.TOOLTIPS.LOST',
  ],
  [
    'won_meetings',
    'DEAL_REPORTS.OPERATIONS.WON_MEETINGS',
    'i-lucide-trophy',
    'DEAL_REPORTS.OPERATIONS.TOOLTIPS.WON_MEETINGS',
  ],
  [
    'outbound_calls',
    'DEAL_REPORTS.OPERATIONS.OUTBOUND_CALLS',
    'i-lucide-phone-outgoing',
    'DEAL_REPORTS.OPERATIONS.TOOLTIPS.OUTBOUND_CALLS',
  ],
  [
    'leads_not_called',
    'DEAL_REPORTS.OPERATIONS.NOT_CALLED',
    'i-lucide-phone-missed',
    'DEAL_REPORTS.OPERATIONS.TOOLTIPS.NOT_CALLED',
  ],
];
</script>

<template>
  <section class="space-y-6">
    <div>
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ $t('DEAL_REPORTS.OPERATIONS.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-10">
        {{ $t('DEAL_REPORTS.OPERATIONS.DESCRIPTION') }}
      </p>
    </div>

    <div class="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
      <div
        v-for="[key, label, icon, tooltip] in metrics"
        :key="key"
        class="flex min-h-28 flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="flex items-start justify-between gap-2">
          <span class="text-lg text-n-blue-10" :class="[icon]" />
          <button
            v-tooltip.top="$t(tooltip)"
            type="button"
            class="cursor-help rounded text-n-slate-9 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-8"
            :aria-label="$t(tooltip)"
          >
            <span class="i-lucide-circle-help block text-base" />
          </button>
        </div>
        <p class="mt-auto pt-3 text-2xl font-semibold text-n-slate-12">
          {{ data.summary?.[key] || 0 }}
        </p>
        <p class="mt-1 text-sm text-n-slate-10">{{ $t(label) }}</p>
        <div
          v-if="key === 'scheduled'"
          class="mt-2 space-y-1 border-t border-n-weak pt-2 text-xs text-n-slate-10"
        >
          <p>
            {{
              $t('DEAL_REPORTS.OPERATIONS.SCHEDULED_FROM_NEW', {
                count: data.summary?.scheduled_from_new_cards || 0,
                total: data.summary?.cards_created || 0,
              })
            }}
          </p>
          <p>
            {{
              $t('DEAL_REPORTS.OPERATIONS.SCHEDULED_FROM_PREVIOUS', {
                count: scheduledFromPreviousCards,
              })
            }}
          </p>
        </div>
      </div>
      <div
        class="flex min-h-28 flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="mb-3 flex items-center justify-between gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('DEAL_REPORTS.OPERATIONS.BY_SDR') }}
          </h3>
          <span
            class="rounded-full bg-n-blue-3 px-2.5 py-1 text-xs font-semibold text-n-blue-11"
          >
            {{ data.summary?.cards_created || 0 }}
            {{ $t('DEAL_REPORTS.OPERATIONS.CARDS') }}
          </span>
        </div>
        <div
          v-if="data.by_sdr?.length"
          class="max-h-24 space-y-2.5 overflow-y-auto pr-1"
        >
          <div
            v-for="row in data.by_sdr"
            :key="row.id || 'unassigned'"
            class="text-sm"
          >
            <div class="mb-1 flex items-center justify-between gap-3">
              <span class="truncate text-xs text-n-slate-11">
                {{ row.name }}
              </span>
              <span class="font-semibold text-n-slate-12">
                {{ row.leads }}
              </span>
            </div>
            <progress
              class="h-1.5 w-full overflow-hidden rounded-full accent-n-blue-9"
              :value="row.leads"
              :max="largestSdrTotal"
            />
          </div>
        </div>
        <p v-else class="text-sm text-n-slate-10">
          {{ $t('DEAL_REPORTS.OPERATIONS.NO_DATA') }}
        </p>
      </div>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="font-semibold text-n-slate-12">
          {{ $t('DEAL_REPORTS.OPERATIONS.NOT_CALLED_CLIENTS') }}
        </h3>
        <span
          class="rounded-full bg-n-amber-3 px-2.5 py-1 text-xs font-semibold text-n-amber-11"
        >
          {{ data.summary?.leads_not_called || 0 }}
        </span>
      </div>
      <div
        class="grid max-h-[32rem] grid-cols-1 gap-3 overflow-y-auto pr-2 md:grid-cols-2"
      >
        <div
          v-for="row in data.not_called"
          :key="row.card_id"
          class="flex min-w-0 items-center justify-between gap-4 rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm"
        >
          <div class="min-w-0">
            <p class="truncate font-medium text-n-slate-12">
              {{ row.contact_name || row.phone }}
            </p>
            <p class="truncate text-xs text-n-slate-10">
              {{ row.phone }} ·
              {{ row.assignee || $t('DEAL_REPORTS.OPERATIONS.UNASSIGNED') }}
            </p>
            <p
              class="mt-1 inline-flex max-w-full rounded bg-n-slate-3 px-2 py-0.5 text-xs text-n-slate-10"
            >
              {{ row.stage_name }}
            </p>
          </div>
          <span class="shrink-0 text-xs text-n-slate-9"
            >#{{ row.conversation_id }}</span
          >
        </div>
        <p
          v-if="!data.not_called?.length"
          class="text-sm text-n-slate-10 md:col-span-2"
        >
          {{ $t('DEAL_REPORTS.OPERATIONS.NO_DATA') }}
        </p>
      </div>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 p-5">
      <h3 class="mb-4 font-semibold text-n-slate-12">
        {{ $t('DEAL_REPORTS.OPERATIONS.DAILY') }}
      </h3>
      <div class="max-h-96 overflow-y-auto pr-2">
        <table class="w-full text-left text-sm">
          <thead class="text-xs uppercase text-n-slate-9">
            <tr>
              <th class="py-2">{{ $t('DEAL_REPORTS.OPERATIONS.DATE') }}</th>
              <th class="py-2 text-right">
                {{ $t('DEAL_REPORTS.OPERATIONS.CARDS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="row in data.daily" :key="row.date">
              <td class="py-3 text-n-slate-11">
                {{ formatDate(row.date) }}
              </td>
              <td class="py-2 text-right font-semibold text-n-slate-12">
                {{ row.cards }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
